import React, { FormEvent, useEffect, useState } from 'react';
import { ROUTE_KEYS } from 'utils/constants';
import { useNavigate, useLocation } from 'react-router';
import AuthService from 'services/auth.service';
import AuthLayout from './auth-layout';
import { Link, useSearchParams } from 'react-router-dom';
import ResetPasswordSuccessModal from 'components/modals/reset-password-success-modal';
import ResetEmailSuccessModal from 'components/modals/reset-email-success-modal';
import { Input } from '@cokitchen/cokitchen-components';
import { handleApiError } from 'utils/functions';

type LoginFormType = {
  email: string;
  password: string;
};

const Login = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [resetSuccessModal, setResetSuccessModal] = useState(false);
  const [emailResetSuccessModal, setEmailResetSuccessModal] = useState(false);
  const [formError, setFormError] = useState<Partial<LoginFormType>>({});
  const [formData, setFormData] = useState<LoginFormType>({
    email: '',
    password: ''
  });
  const expired = new URLSearchParams(useLocation().search).get('expired') === 'true';
  const formIsValid = (): boolean => {
    setFormError({});
    const email = formData.email.trim();
    const password = formData.password.trim();
    if (email !== '' && password !== '') return true;
    if (email === '') setFormError({ email: 'Email is required' });
    if (password === '') {
      setFormError((p) => ({ ...p, password: 'Password is required' }));
    }
    return false;
  };

  const login = async (e: FormEvent) => {
    e.preventDefault();
    if (!formIsValid()) return;
    setLoading(true);
    try {
      await AuthService.login(formData);
      if (!(await AuthService.isCompanyVerified())) {
        return navigate(ROUTE_KEYS.VERIFICATION_PENDING);
      }
      navigate(`/${ROUTE_KEYS.HOME}`);
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (key: string, value: string) => {
    setFormError((p) => ({ ...p, [key]: false }));
    setFormData((p) => ({ ...p, [key]: value }));
  };

  const handleSuccessModal = () => {
    searchParams.delete('from');
    setSearchParams(searchParams);
    setResetSuccessModal(false);
    setEmailResetSuccessModal(false);
  };

  useEffect(() => {
    if (search.includes('reset-password') || search.includes('create-new-password'))
      setResetSuccessModal(true);
    if (search.includes('verify-email')) setEmailResetSuccessModal(true);
  }, []);

  return (
    <section className=''>
      <AuthLayout
        title={'Welcome back'}
        onConfirm={login}
        loading={loading}
        onConfirmText='Login'
        subText={
          <p className='text-[#6F7376] mt-4'>
            Don&apos;t have an account?
            <Link
              className='cursor-pointer ml-1 text-[#2E3031] font-medium '
              to={`/${ROUTE_KEYS.AUTH}/${ROUTE_KEYS.REGISTER}`}>
              Register
            </Link>
          </p>
        }
        subtitle={`Login to your foodcourt business account to continue putting a smile on people’s faces`}>
        <div className='flex flex-col gap-7'>
          <Input
            design='bordered'
            placeholder='Email'
            label='Email'
            type='email'
            value={formData.email}
            onChange={(e) => updateFormData('email', e.target.value)}
            error={formError.email}
          />
          <Input
            design='bordered'
            placeholder='Password'
            label='Password'
            type='password'
            value={formData.password}
            onChange={(e) => updateFormData('password', e.target.value)}
            error={formError.password}
          />
          <Link
            to={`/${ROUTE_KEYS.AUTH}/${ROUTE_KEYS.FORGOT_PASSWORD}`}
            className='font-medium cursor-pointer text-[#0080E5]'>
            Forgot your password?
          </Link>
          {expired && (
            <div className='absolute right-0 top-5 bg-[#E60A2B] text-white px-6 py-4 text-sm font-medium '>
              <p>Your session has expired </p>
              <p>Login to continue</p>
            </div>
          )}
        </div>
      </AuthLayout>

      {resetSuccessModal ? (
        <ResetPasswordSuccessModal isOpen={resetSuccessModal} onClose={handleSuccessModal} />
      ) : null}
      {emailResetSuccessModal ? (
        <ResetEmailSuccessModal isOpen={emailResetSuccessModal} onClose={handleSuccessModal} />
      ) : null}
    </section>
  );
};

export default Login;
